/**
 * BRE-9232: the one list of Brev's keyboard shortcuts. The `?` sheet renders it,
 * and a bare-key registration reads its key from here (`shortcutKey`), so the
 * sheet and the binding cannot drift apart. To add a shortcut, add a row here
 * and bind it with the same row's key.
 *
 * `keys` are tokens: "Mod" is Cmd on Apple platforms and Ctrl elsewhere, "Shift"
 * is Shift, and anything else is shown as written.
 */
export const SHORTCUT_SCOPES = [
  "Everywhere",
  "Go to",
  "Command palette",
  "Lists",
  "Goal panel",
  "Timeline",
  "Artifacts",
  "Settings",
] as const;
export type ShortcutScope = (typeof SHORTCUT_SCOPES)[number];

export interface ShortcutDef {
  id: string;
  keys: readonly string[];
  label: string;
  scope: ShortcutScope;
  /** Keys are pressed one after another (G, then A), not held together. */
  sequence?: boolean;
  /** Extra words the sheet's search matches that the label does not contain. */
  keywords?: readonly string[];
  /** `KeyboardEvent.code` for a combination whose `key` changes under Shift (Shift+, is "<") or by layout. */
  code?: string;
  /** A `use:*` grant the viewer needs to see this row: the binding it lists only acts with that grant. */
  permission?: string;
}

export const SHORTCUTS: readonly ShortcutDef[] = [
  { id: "shortcut-sheet", keys: ["?"], label: "Show keyboard shortcuts", scope: "Everywhere" },
  { id: "command-palette", keys: ["Mod", "K"], label: "Open the command palette", scope: "Everywhere" },
  { id: "toggle-sidebar", keys: ["Mod", "B"], label: "Collapse or expand the sidebar", scope: "Everywhere" },
  { id: "toggle-theme", keys: ["D"], label: "Switch between light and dark", scope: "Everywhere", keywords: ["theme"] },
  { id: "create", keys: ["C"], label: "Open the page's create dialog", scope: "Everywhere" },
  { id: "dialog-submit", keys: ["Mod", "Enter"], label: "Submit the open dialog", scope: "Everywhere" },
  { id: "quick-add-action", keys: ["Q"], label: "New action from any page", scope: "Everywhere", keywords: ["quick add", "capture"] },
  { id: "history-back", keys: ["Mod", "["], label: "Go back", scope: "Everywhere", keywords: ["history", "previous page"], code: "BracketLeft" },
  { id: "history-forward", keys: ["Mod", "]"], label: "Go forward", scope: "Everywhere", keywords: ["history", "next page"], code: "BracketRight" },
  { id: "recent-switcher", keys: ["Mod", "P"], label: "Switch to a recent item", scope: "Everywhere", keywords: ["recents", "quick switcher"] },
  { id: "goto-goals", keys: ["G", "G"], label: "Go to Goals", scope: "Go to", sequence: true },
  { id: "goto-actions", keys: ["G", "A"], label: "Go to My actions", scope: "Go to", sequence: true },
  { id: "goto-cadences", keys: ["G", "C"], label: "Go to Cadences", scope: "Go to", sequence: true },
  { id: "goto-artifacts", keys: ["G", "T"], label: "Go to Artifacts", scope: "Go to", sequence: true },
  { id: "goto-inbox", keys: ["G", "I"], label: "Go to Notifications", scope: "Go to", sequence: true },
  { id: "goto-home", keys: ["G", "H"], label: "Go to Home", scope: "Go to", sequence: true },
  { id: "goto-reports", keys: ["G", "R"], label: "Go to Reports (admins)", scope: "Go to", sequence: true },
  { id: "goto-settings", keys: ["G", "S"], label: "Go to Settings", scope: "Go to", sequence: true },
  { id: "palette-commands", keys: [">"], label: "Type first to list commands only", scope: "Command palette" },
  { id: "palette-back", keys: ["Backspace"], label: "Back to the previous step, from an empty search", scope: "Command palette" },
  { id: "list-peek", keys: ["Space"], label: "Peek at the row under the pointer, again to close", scope: "Lists" },
  { id: "list-next", keys: ["J"], label: "Move to the next row", scope: "Lists", keywords: ["down"] },
  { id: "list-previous", keys: ["K"], label: "Move to the previous row", scope: "Lists", keywords: ["up"] },
  { id: "list-select", keys: ["X"], label: "Select or unselect the row", scope: "Lists", keywords: ["check", "bulk"] },
  { id: "list-open", keys: ["Enter"], label: "Open the goal, or rename the action", scope: "Lists" },
  { id: "list-status", keys: ["S"], label: "Change status", scope: "Lists", keywords: ["selected rows"] },
  { id: "list-assign", keys: ["A"], label: "Change owner or assignee", scope: "Lists", keywords: ["assign", "selected rows"] },
  { id: "list-labels", keys: ["L"], label: "Change labels", scope: "Lists", keywords: ["tags", "selected rows"] },
  { id: "edit-goal-under-pointer", keys: ["E"], label: "Edit the goal under the pointer", scope: "Lists", keywords: ["board", "hover"], permission: "use:edit-goal-under-pointer" },
  { id: "goal-panel-next", keys: ["↓"], label: "Next goal", scope: "Goal panel" },
  { id: "goal-panel-previous", keys: ["↑"], label: "Previous goal", scope: "Goal panel" },
  { id: "goal-parent", keys: ["Mod", "↑"], label: "Go to the parent goal", scope: "Goal panel", keywords: ["up a level"], code: "ArrowUp" },
  { id: "copy-goal-link", keys: ["Mod", "Shift", ","], label: "Copy the goal's link", scope: "Goal panel", code: "Comma" },
  { id: "timeline-expand", keys: ["Space"], label: "Expand the bar under the pointer", scope: "Timeline" },
  { id: "timeline-edit-goal", keys: ["E"], label: "Edit the goal under the pointer", scope: "Timeline" },
  { id: "undo", keys: ["Mod", "Z"], label: "Undo", scope: "Artifacts" },
  { id: "redo", keys: ["Mod", "Shift", "Z"], label: "Redo", scope: "Artifacts" },
  { id: "text-indent", keys: ["Tab"], label: "Indent the text", scope: "Artifacts", permission: "use:artifact-rich-text" },
  { id: "text-outdent", keys: ["Shift", "Tab"], label: "Outdent the text", scope: "Artifacts", permission: "use:artifact-rich-text" },
  { id: "text-underline", keys: ["Mod", "U"], label: "Underline", scope: "Artifacts", permission: "use:artifact-rich-text" },
  { id: "text-highlight", keys: ["Mod", "Shift", "H"], label: "Highlight", scope: "Artifacts", permission: "use:artifact-rich-text" },
  { id: "text-leave", keys: ["Esc"], label: "Leave the text", scope: "Artifacts", permission: "use:artifact-rich-text" },
  { id: "settings-search", keys: ["Mod", "Shift", "P"], label: "Search settings", scope: "Settings" },
];

/** The key a single-key binding listens for: the last token of its row. */
export function shortcutKey(id: string): string {
  const row = SHORTCUTS.find((s) => s.id === id);
  if (!row) throw new Error(`Unknown shortcut: ${id}`);
  return row.keys[row.keys.length - 1];
}

/** The `KeyboardEvent.code` of a row declared with one. */
export function shortcutCode(id: string): string {
  const code = SHORTCUTS.find((s) => s.id === id)?.code;
  if (!code) throw new Error(`Shortcut has no code: ${id}`);
  return code;
}

/** The caps to draw for a combination on this platform. */
export function renderShortcutKeys(keys: readonly string[], apple: boolean): string[] {
  return keys.map((k) => {
    if (k === "Mod") return apple ? "⌘" : "Ctrl";
    if (k === "Shift") return apple ? "⇧" : "Shift";
    return k;
  });
}

/** Rows whose label, scope, keys or keywords contain every word of `query` (all rows for a blank query). */
export function filterShortcuts(query: string, shortcuts: readonly ShortcutDef[] = SHORTCUTS): ShortcutDef[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...shortcuts];
  return shortcuts.filter((s) => {
    const haystack = `${s.label} ${s.scope} ${s.keys.join(" ")} ${(s.keywords ?? []).join(" ")}`.toLowerCase();
    return words.every((w) => haystack.includes(w));
  });
}

/** Non-empty scopes in display order, each with its rows. */
export function groupShortcuts(shortcuts: readonly ShortcutDef[]): { scope: ShortcutScope; shortcuts: ShortcutDef[] }[] {
  return SHORTCUT_SCOPES.map((scope) => ({ scope, shortcuts: shortcuts.filter((s) => s.scope === scope) })).filter(
    (g) => g.shortcuts.length > 0
  );
}
